import React, { useState } from 'react';
import { Check } from 'lucide-react';
import { authAPI } from '../../api';
import { log } from '../../utils/logger';
import { redirectToMarketing } from '../../config/domains';

const ForgotPasswordScreen = ({ onBackToLogin }) => {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [status, setStatus] = useState('');
  const [showConfirmation, setShowConfirmation] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setStatus('');

    try {
      log.info("Sending reset email to:", email);
      const response = await authAPI.resetPassword(email);

      if (!response.success) {
        throw response.error;
      }

      setStatus('Password reset email sent! Check your inbox.');
      setShowConfirmation(true);
    } catch (err) {
      // Error caught and ignored
      log.error("Error sending reset email:", err);
      setStatus(err.message || 'Failed to send reset email.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResend = async () => {
    setIsLoading(true);
    setStatus('');

    try {
      log.info("Resending reset email to:", email);
      const response = await authAPI.resetPassword(email);

      if (!response.success) {
        throw response.error;
      }

      setStatus('Password reset email sent! Check your inbox.');
    } catch (err) {
      // Error caught and ignored
      log.error("Error resending reset email:", err);
      setStatus(err.message || 'Failed to send reset email.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md">
      <div className="text-center mb-8">
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            redirectToMarketing();
          }}
          className="inline-block cursor-pointer hover:opacity-80 transition-opacity bg-transparent border-0 p-0"
          title="Go to TeamRezy homepage"
        >
          <img src="/TeamRezyLogo.png" alt="TeamRezy Logo" className="mx-auto mb-4 h-48 w-auto object-contain" />
        </button>
        <p className="text-gray-600">Streamline your team dining experience</p>
      </div>

      {showConfirmation ? (
        <div className="space-y-4 mb-4">
          <div className="text-center mb-4">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-green-100 mb-3">
              <Check className="w-6 h-6 text-green-600" />
            </div>
            <h3 className="text-lg font-medium text-gray-900">Check your email</h3>
            <p className="text-sm text-gray-600 mt-1">
              We've sent a password reset link to <span className="font-medium">{email}</span>
            </p>
          </div>

          {status && <div className={`text-sm ${status.includes('sent') ? 'text-green-600' : 'text-red-600'}`}>{status}</div>}

          <button
            type="button"
            className="w-full bg-navy-600 text-white py-3 rounded-lg hover:bg-navy-800 transition-colors disabled:bg-gray-400"
            onClick={handleResend}
            disabled={isLoading}
            style={{ border: 'none' }}
          >
            {isLoading ? 'Sending...' : 'Resend Email'}
          </button>

          <button
            type="button"
            className="w-full mt-2 text-navy-600 hover:text-blue-700 text-sm"
            onClick={onBackToLogin}
            style={{ border: 'none' }}
          >
            Back to Login
          </button>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4 mb-4">
          <input
            id="forgot-password-email"
            type="email"
            name="email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            placeholder="Enter your email"
            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            required
            autoComplete="email"
          />

          {status && <div className={`text-sm ${status.includes('sent') ? 'text-green-600' : 'text-red-600'}`}>{status}</div>}

          <button
            type="submit"
            className="w-full bg-navy-600 text-white py-3 rounded-lg hover:bg-navy-800 transition-colors disabled:bg-gray-400"
            disabled={isLoading}
            style={{ border: 'none' }}
          >
            {isLoading ? 'Sending...' : 'Send Reset Email'}
          </button>

          <button
            type="button"
            className="w-full mt-2 text-navy-600 hover:text-blue-700 text-sm"
            onClick={onBackToLogin}
            style={{ border: 'none' }}
          >
            Back to Login
          </button>
        </form>
      )}
    </div>
  );
};

export default ForgotPasswordScreen;
